/// <amd-module name="VSSUI/Components/ListBox/ListBox" />
import * as React from "react";
import { ObservableLike } from "VSS/Core/Observable";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { Icon } from "VSSUI/Icon";
import { IListSelection, renderListCell } from "VSSUI/List";
import { IObservableExpression, ItemsObserver, Observer } from "VSSUI/Observer";
import * as Resources from "VSSUI/Resources.Dropdown";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ColumnSelect, ITableColumn, ITableRow, ITableRowDetails, renderEmptyCell, SimpleTableCell, Table, TableRow } from "VSSUI/Table";
import { ITreeColumn, ITreeRow, ITreeRowDetails, Tree, TreeRow } from "VSSUI/TreeEx";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { ArrayItemProvider, getItemsValue, IItemProvider } from "VSSUI/Utilities/Provider";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { ITreeItem, ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { IListBoxItem, IListBoxProps, ILoadingCellProps, ListBoxItemType } from "./ListBox.Props";

export const DefaultListBoxWidth = -100;

export class ListBox<T> extends React.Component<IListBoxProps<T>> {
    public static defaultProps = {
        getUnselectableRanges: getUnselectableRanges,
        width: DefaultListBoxWidth
    };

    private columns: (ColumnSelect | ITableColumn<{}>)[];
    private tabbableIndex: number = -1;
    private positions: number[] = [];
    private count: number = 0;
    private multiSelect: boolean;
    private selection: IListSelection;
    private wrappedItems: IListBoxItem<T>[] | undefined;
    private table = React.createRef<Table<IListBoxItem<T>>>();
    private tree = React.createRef<Tree<IListBoxItem<T>>>();

    constructor(props: IListBoxProps<T>) {
        super(props);

        const { selection, renderItem, items } = this.props;

        this.selection = selection || new DropdownSelection();
        this.multiSelect = this.props.enforceSingleSelect || this.props.showTree ? false : this.selection.multiSelect;

        // trees get custom columns
        if (!this.props.columns) {
            this.columns = [];
            if (this.multiSelect && this.props.showChecksColumn !== false) {
                this.columns.push(new ColumnSelect({ role: "presentation", excludeFocusZone: true }));
            } else if (!this.multiSelect && this.props.showChecksColumn === true) {
                this.columns.push({
                    id: "column-check",
                    width: 24,
                    renderCell: (rowIndex: number, columnIndex: number) => (
                        document.body.classList.contains('dropdown-list-component-enabled') ? (
                            <div className="dropdown-list checkmark-icon" data-column-index={columnIndex} key="column-check" role="presentation">
                                <Icon ariaHidden={"removed"} className={css(!this.selection.selected(rowIndex) && "invisible")} iconName="CheckMark" />
                            </div>
                        ) : (
                            <SimpleTableCell columnIndex={columnIndex} key="column-check" role="presentation">
                                <Icon className={css(!this.selection.selected(rowIndex) && "invisible")} iconName="CheckMark" />
                            </SimpleTableCell>
                        )
                    ),
                    readonly: true
                });
            }

            this.columns.push({
                id: "text",
                width: this.getItemWidth(),
                renderCell: renderItem || this.renderListBoxCell,
                className: css("bolt-list-box-text", this.multiSelect ? "bolt-list-box-text-multi-select" : "bolt-list-box-text-single-select"),
                readonly: true
            });
        } else {
            this.columns = this.props.columns;
        }

        // string items are wrapped once here.  Only use a string array in the simple case where the items are not changing.
        this.wrappedItems = wrapListBoxItems(items);
        this.onItemsChanged();
    }

    public componentDidUpdate() {
        if (this.props.didUpdate) {
            this.props.didUpdate();
        }
    }

    public render() {
        const {
            ariaLabel,
            className,
            containerClassName,
            enforceSingleSelect,
            focuszoneProps,
            getUnselectableRanges,
            items,
            loading,
            searching,
            searchResultsLoadingText,
            showItemsWhileSearching,
            width
        } = this.props;
        const itemsObservable = { observableValue: items, filter: this.onItemsChanged };
        const listBoxItems = this.getListBoxItems();
        const itemProvider = listBoxItems
            ? this.props.showTree
                ? new TreeItemProvider(convertListBoxItemsToTreeItems(listBoxItems))
                : new ArrayItemProvider(listBoxItems)
            : items;

        if (!this.props.columns) {
            this.columns[this.columns.length - 1].width = this.getItemWidth();
        }

        return (
            <ItemsObserver<IListBoxItem<T> | string> getUnselectableRanges={getUnselectableRanges!} items={items} selection={this.selection}>
                <Observer
                    items={itemsObservable}
                    loading={{ observableValue: loading || false, filter: this.loadingChanged } as IObservableExpression<boolean>}
                    searching={{ observableValue: searching || false, filter: this.searchingChanged } as IObservableExpression<boolean>}
                >
                    {(props: { loading: boolean; searching: boolean }) => {
                        if (props.searching && !showItemsWhileSearching) {
                            return (
                                <div className="bolt-list-box-loading" style={{ width }}>
                                    <Spinner size={SpinnerSize.medium} label={searchResultsLoadingText || Resources.Searching} />
                                </div>
                            );
                        }

                        if (this.props.showTree) {
                            const treeProvider = itemProvider as TreeItemProvider<IListBoxItem<T>>;
                            return (
                                <Tree<IListBoxItem<T>>
                                    ariaLabel={ariaLabel || undefined}
                                    className={css(className, "bolt-list-box", "bolt-list-box-tree")}
                                    columns={this.columns}
                                    containerClassName={containerClassName}
                                    focuszoneProps={focuszoneProps}
                                    itemProvider={treeProvider}
                                    onActivate={this.onTreeActivate}
                                    onSelect={this.onTreeSelect}
                                    onToggle={(event: React.SyntheticEvent<HTMLElement, Event>, treeItem: ITreeItemEx<IListBoxItem<T>>) => {
                                        if ((event.target as HTMLSpanElement).className.includes("bolt-tree-expand-button")) {
                                            treeProvider.toggle(treeItem.underlyingItem);
                                            if (this.props.onToggle) {
                                                this.props.onToggle(event, treeItem.underlyingItem.data);
                                            }
                                        }
                                    }}
                                    ref={this.tree}
                                    renderRow={this.renderListBoxTreeRow}
                                    role={"listbox"}
                                    scrollable={true}
                                    singleClickActivation={false}
                                    selection={this.selection}
                                    showHeader={false}
                                    showLines={false}
                                />
                            );
                        } else {
                            return (
                                <Table<IListBoxItem<T>>
                                    ariaLabel={ariaLabel || undefined}
                                    className={css(className, "bolt-list-box")}
                                    columns={this.columns}
                                    containerClassName={containerClassName}
                                    enforceSingleSelect={enforceSingleSelect}
                                    focuszoneProps={focuszoneProps}
                                    itemProvider={itemProvider as IItemProvider<IListBoxItem<T>>}
                                    onActivate={this.onActivate}
                                    onSelect={this.onSelect}
                                    renderRow={this.renderListBoxRow}
                                    ref={this.table}
                                    role={"listbox"}
                                    scrollable={true}
                                    singleClickActivation={false}
                                    selection={this.selection}
                                    showHeader={false}
                                    showLines={false}
                                    spacerWidth={0}
                                />
                            );
                        }
                    }}
                </Observer>
            </ItemsObserver>
        );
    }

    public scrollIntoView(rowIndex: number, options?: ScrollIntoViewOptions): void {
        if (this.table.current) {
            return this.table.current.scrollIntoView(rowIndex, options);
        } else if (this.tree.current) {
            return this.tree.current.scrollIntoView(rowIndex, options);
        }
    }

    /**
     * Try to pull list box items out of props and variables.
     * Returns undefined in case where IItemProvider was passed in.
     */
    private getListBoxItems(): IListBoxItem<T>[] | undefined {
        return (
            this.wrappedItems ??
            (this.props.items && ((Array.isArray(this.props.items) ? this.props.items : this.props.items.value) as IListBoxItem<T>[])) ??
            undefined
        );
    }

    private getItemWidth = (): number => {
        const { width } = this.props;
        return this.multiSelect && width && width > 0 ? width - 40 /* TODO: Remove this, 40 is only correct with default font-size */ : width!;
    };

    private loadingChanged = (): boolean => {
        if (ObservableLike.getValue(this.props.loading)) {
            Utils_Accessibility.announce(Resources.AnnounceLoadingItems);
        } else {
            Utils_Accessibility.announce(Resources.AnnounceFinishedLoadingItems);
            const itemCount = this.props.items.length;
            Utils_Accessibility.announce(format(itemCount > 0 ? format(Resources.AnnounceItemCount, itemCount) : Resources.NoFilterResults), true);
        }
        return true;
    };

    private searchingChanged = (): boolean => {
        if (ObservableLike.getValue(this.props.searching)) {
            Utils_Accessibility.announce(Resources.Searching);
        } else if (ObservableLike.getValue(this.props.searching) === false) {
            const resultCount = this.props.items.length;
            Utils_Accessibility.announce(
                resultCount > 0 ? format(Resources.AnnounceFilterResultCount, resultCount) : Resources.NoFilterResults,
                true
            );
        }
        return true;
    };

    private onItemsChanged = (): boolean => {
        const items = getListBoxItemsValue(this.wrappedItems || this.props.items);
        this.tabbableIndex = -1;
        this.positions = [];
        this.count = 0;
        for (const item of items) {
            const itemValue = ObservableLike.getValue(item);
            if (itemValue && !listBoxItemSelectable(itemValue)) {
                this.positions.push(-1);
            } else {
                if (this.tabbableIndex === -1 && this.selection.selectable(this.positions.length)) {
                    this.tabbableIndex = this.positions.length;
                }
                this.positions.push(++this.count);
            }
        }
        return true;
    };

    private onActivate = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<IListBoxItem<T>>) => {
        if (this.props.onActivate) {
            this.props.onActivate(event, tableRow.data);
        }
    };

    private onSelect = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<IListBoxItem<T>>) => {
        if (this.props.onSelect) {
            this.props.onSelect(event, tableRow.data);
        }
    };

    private onTreeActivate = (event: React.SyntheticEvent<HTMLElement>, treeRow: ITreeRow<IListBoxItem<T>>) => {
        const items = this.getListBoxItems();
        if (this.props.onActivate && items) {
            const treeItem = treeRow.data.underlyingItem;
            const item = items.find(item => item.id === treeItem.id);
            item && this.props.onActivate(event, item);
        }
    };

    private onTreeSelect = (event: React.SyntheticEvent<HTMLElement>, treeRow: ITreeRow<IListBoxItem<T>>) => {
        const items = this.getListBoxItems();
        if (this.props.onSelect && items) {
            const treeItem = treeRow.data.underlyingItem;
            const item = items.find(item => item.id === treeItem.id);
            item && this.props.onSelect(event, item);
        }
    };

    private renderListBoxRow = (index: number, item: IListBoxItem<T>, details: ITableRowDetails<IListBoxItem<T>>): JSX.Element => {
        const { excludeFocusZone, excludeTabStop } = this.props;
        const items = getListBoxItemsValue(this.wrappedItems || this.props.items);
        const focusable = !excludeFocusZone && this.selection.selectable(index);
        const rowDetails = {
            tooltipProps: item.tooltipProps || { text: item.text, overflowOnly: true, overflowDetected: overflowDetected },
            ...details,
            ariaDescribedBy: item.type !== ListBoxItemType.Divider && item.type !== ListBoxItemType.Header ? `header-${item.groupId}` : undefined,
            ariaPosInSet: this.positions[index] >= 0 ? this.positions[index] : null,
            ariaSetSize: this.positions[index] >= 0 ? this.count : null,
            excludeTabStop: excludeTabStop || this.tabbableIndex !== index,
            excludeFocusZone: !focusable,
            id: item.id,
            singleClickActivation: false,
            role: item.type === ListBoxItemType.Header || item.type === ListBoxItemType.Divider ? "presentation" : "option"
        };
        return (
            <Observer key={item.id || index} item={item}>
                {() => (
                    <TableRow
                        key={item.id || index}
                        index={index}
                        details={rowDetails}
                        className={css(
                            "bolt-list-box-row",
                            item.type === ListBoxItemType.Header && "bolt-list-box-header-row",
                            item.type === ListBoxItemType.Divider && "bolt-list-box-divider-row",
                            item.type === ListBoxItemType.Loading && "bolt-list-box-loading-row",
                            this.multiSelect && "bolt-list-box-multi-select-row",
                            item.type !== ListBoxItemType.Header && item.type !== ListBoxItemType.Divider && "cursor-pointer",
                            item.disabled && "bolt-list-box-item-disabled"
                        )}
                    >
                        {this.columns.map((tableColumn: ITableColumn<IListBoxItem<T>>, columnIndex: number) => {
                            if (this.multiSelect && columnIndex === 0) {
                                if (item.type === ListBoxItemType.Divider || item.type === ListBoxItemType.Loading) {
                                    return null;
                                } else if (item.type === ListBoxItemType.Header) {
                                    return document.body.classList.contains('dropdown-list-component-enabled') ? (<span className="bolt-table-cell bolt-list-cell bolt-header-cell" data-column-index="0" role="presentation"></span>) : renderEmptyCell(index, columnIndex);
                                }
                            }
                            if (document.body.classList.contains('dropdown-list-component-enabled')) {
                                tableColumn = { ...tableColumn, className: "dropdown-list"}
                            }
                            return tableColumn.renderCell(index, columnIndex, tableColumn, item);
                        })}
                    </TableRow>
                )}
            </Observer>
        );
    };

    private renderListBoxTreeRow = (index: number, item: ITreeItemEx<IListBoxItem<T>>, details: ITreeRowDetails<IListBoxItem<T>>): JSX.Element => {
        const { excludeFocusZone, excludeTabStop } = this.props;
        const focusable = !excludeFocusZone && this.selection.selectable(index);
        const data = item.underlyingItem.data;
        const rowDetails = {
            tooltipProps: { text: item.underlyingItem.text, overflowOnly: true, overflowDetected },
            ...details,
            ariaPosInSet: this.positions[index] >= 0 ? this.positions[index] : null,
            ariaSetSize: this.positions[index] >= 0 ? this.count : null,
            excludeTabStop: excludeTabStop || this.tabbableIndex !== index,
            excludeFocusZone: !focusable,
            id: item.underlyingItem.id,
            singleClickActivation: false,
            role: data.type === ListBoxItemType.Header || data.type === ListBoxItemType.Divider ? "row" : "treeitem"
        };

        return (
            <Observer key={`observer-${item.underlyingItem.id || index}`} item={item}>
                {() => (
                    <TreeRow
                        key={`row-${item.underlyingItem.id || index}`}
                        index={index}
                        details={rowDetails}
                        className={css(
                            "bolt-list-box-row",
                            data.type === ListBoxItemType.Header && "bolt-list-box-header-row",
                            data.type === ListBoxItemType.Divider && "bolt-list-box-divider-row",
                            data.type === ListBoxItemType.Loading && "bolt-list-box-loading-row",
                            this.multiSelect && "bolt-list-box-multi-select-row",
                            data.type !== ListBoxItemType.Header && data.type !== ListBoxItemType.Divider && "cursor-pointer",
                            data.disabled && "bolt-list-box-item-disabled"
                        )}
                    >
                        {this.columns.map((treeColumn: ITreeColumn<IListBoxItem<T>>, columnIndex: number) => {
                            return treeColumn.renderCell(index, columnIndex, treeColumn, item);
                        })}
                    </TreeRow>
                )}
            </Observer>
        );
    };

    private renderListBoxCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IListBoxItem<T>>,
        tableItem: IListBoxItem<T>
    ): JSX.Element => {
        return renderListBoxCell(rowIndex, columnIndex, tableColumn, tableItem, this.multiSelect);
    };
}

export function renderListBoxCell<T>(
    rowIndex: number,
    columnIndex: number,
    tableColumn: ITableColumn<IListBoxItem<T>>,
    tableItem: IListBoxItem<T>,
    multiSelect?: boolean
): JSX.Element {
    if (tableItem.render) {
        return tableItem.render(rowIndex, columnIndex, tableColumn, tableItem);
    }
    if (tableItem.type === ListBoxItemType.Divider) {
        return (
            document.body.classList.contains('dropdown-list-component-enabled') ? (
                // We don't need the table cell here. Once we move the ListBox to its own markup, this will be more like how we'd construct its contents.
                new ColumnSelect({ role: "presentation", excludeFocusZone: true }),
                <div className="bolt-list-box-divider flex-grow" />
            ) : (
                <SimpleTableCell
                    className={css(tableColumn.className, tableItem.className, multiSelect && "bolt-list-box-divider-multi-select")}
                    columnIndex={columnIndex}
                    colspan={multiSelect ? 2 : 1}
                    key={tableItem.id}
                    role="presentation"
                    tableColumn={tableColumn}
                >
                    <div className="bolt-list-box-divider flex-grow" />
                </SimpleTableCell>
            )
        );
    } else if (tableItem.type === ListBoxItemType.Loading) {
        return <LoadingCell<T> columnIndex={columnIndex} key={tableItem.id} tableColumn={tableColumn} tableItem={tableItem} />;
    }
    return (
        <SimpleTableCell
            className={css(tableColumn.className, tableItem.className, tableItem.type === ListBoxItemType.Header && "bolt-list-box-header")}
            columnIndex={columnIndex}
            key={tableItem.id}
            role="presentation"
            tableColumn={tableColumn}
        >
            <div
                id={tableItem.type === ListBoxItemType.Header ? `header-${tableItem.groupId}` : undefined}
                aria-label={tableItem.type === ListBoxItemType.Header && !document.body.classList.contains('dropdown-list-component-enabled') ? format(Resources.HeaderAriaLabel, tableItem.text) : undefined}
                className="bolt-list-box-cell-container"
            >
                {tableItem && renderListCell(tableItem, false)}
            </div>
        </SimpleTableCell>
    );
}

function overflowDetected(anchorElement: HTMLElement): boolean {
    const overflowElement = anchorElement.querySelector<HTMLElement>(".text-ellipsis");
    if (overflowElement) {
        return overflowElement.scrollWidth > Math.ceil(overflowElement.offsetWidth);
    }
    return false;
}

/**
 * Retrieve a list of unselectable ranges based on a itemSelectable function.
 * @param items the set of items
 * @param itemSelectable A function that returns false when an items is not selectable.
 *        Defaults to checking that the item type is not header or divider.
 */
export function getUnselectableRanges<T>(
    items: IListBoxItem<T>[],
    itemSelectable: (item: IListBoxItem<T>) => boolean = listBoxItemSelectable
): ISelectionRange[] {
    const ranges: ISelectionRange[] = [];
    let beginIndex = -1;
    for (let index = 0; index < items.length; index++) {
        if (!itemSelectable(items[index]) && beginIndex < 0) {
            beginIndex = index;
        } else if (itemSelectable(items[index]) && beginIndex >= 0) {
            ranges.push({ beginIndex: beginIndex, endIndex: index - 1 });
            beginIndex = -1;
        }
    }

    if (beginIndex >= 0) {
        ranges.push({ beginIndex: beginIndex, endIndex: items.length - 1 });
    }
    return ranges;
}

/**
 * Return whether a ListBoxItem can be selected or not.
 * @param item the ListBoxItem to evaluate
 */
export function listBoxItemSelectable<T>(item: IListBoxItem<T>) {
    return item.type !== ListBoxItemType.Header && item.type !== ListBoxItemType.Divider && item.type !== ListBoxItemType.Loading && !item.disabled;
}

/**
 * When items is a string[], wrap each item in a ListBoxItem.  Otherwise, do nothing.
 * @param items the items prop
 */
export function wrapListBoxItems<T>(items: string[] | IListBoxItem<T>[] | IItemProvider<IListBoxItem<T>>): IListBoxItem<T>[] | undefined {
    if (Array.isArray(items) && items.length && typeof items[0] === "string") {
        return (items as string[]).map((item: string): IListBoxItem<T> => {
            return { id: item, text: item };
        });
    }
}

/**
 * Walk through the ListBoxItems and construct a tree
 */
export function convertListBoxItemsToTreeItems<T>(items: IListBoxItem<T>[]): ITreeItem<IListBoxItem<T>>[] {
    const rootItems: ITreeItem<IListBoxItem<T>>[] = [];
    const itemsMap = new Map<string, ITreeItem<IListBoxItem<T>>>();

    for (const item of items) {
        // include children that may have been processed before this parent
        const precreatedTreeItem = itemsMap.get(item.id);
        const treeItem = {
            childItems: precreatedTreeItem?.childItems,
            expanded: item.expanded,
            data: item,
            id: item.id,
            text: item.text
        };

        if (!item.parent) {
            rootItems.push(treeItem);
        } else {
            const parent = itemsMap.get(item.parent.id);
            if (parent) {
                if (parent.childItems) {
                    parent.childItems.push(treeItem);
                } else {
                    parent.childItems = [treeItem];
                }
            } else {
                // add a placeholder that tracks children until parent actually gets processed
                itemsMap.set(item.parent.id, { childItems: [treeItem] } as ITreeItem<IListBoxItem<T>>);
            }
        }

        itemsMap.set(treeItem.id, treeItem);
    }

    return rootItems;
}

/**
 * Helper to get the value of the items prop.  If items is a string[], it should first be wrapped using wrapListBoxItems.
 * If it's an itemProvider, .value will be called on the provider.
 * @param items the items prop.  If items was provided as a string[], it should first be wrapped using wrapListBoxItems.
 */
export function getListBoxItemsValue<T>(items: string[] | IListBoxItem<T>[] | IItemProvider<IListBoxItem<T>>): IListBoxItem<T>[] {
    if ($DEBUG) {
        if (Array.isArray(items) && items.length && typeof items[0] === "string") {
            console.warn("a string[] was passed for items and not wrapped first.  Call wrapListBoxItems on items and pass in the results as items.");
        }
    }
    return getItemsValue(items as IListBoxItem<T>[] | IItemProvider<IListBoxItem<T>>);
}

export class LoadingCell<T> extends React.Component<ILoadingCellProps<T>> {
    public componentDidMount() {
        if (this.props.onMount) {
            this.props.onMount();
        }
    }

    public render() {
        const { columnIndex, tableColumn, tableItem } = this.props;
        return (
            <SimpleTableCell
                className={css(tableColumn.className, tableItem.className)}
                columnIndex={columnIndex}
                colspan={2}
                contentClassName="justify-center"
                key={columnIndex}
                tableColumn={tableColumn}
            >
                <div className="bolt-list-box-loading">
                    <Spinner size={SpinnerSize.medium} label={Resources.Loading} />
                </div>
            </SimpleTableCell>
        );
    }
}

export function isListBoxItemVisible<T>(item: IListBoxItem<T>): boolean {
    let parent = item.parent;
    while (parent) {
        if (!parent.expanded) {
            return false;
        }

        parent = parent.parent;
    }

    return true;
}
